// MetricsPage — /metrics.
//
// Mirrors `tricivo_system/operations/metrics.php` (legacy) — KPIs + per-source
// + per-status + per-driver breakdowns over a date range. Rather than build
// a new server endpoint, it composes four of the existing 16 registered
// reports (bookings_by_status, bookings_by_source, bookings_by_driver,
// monthly_summary) to assemble the same KPIs the legacy page showed.

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useRunReport, type ReportTable } from '../useReports';
import {
  formatCell,
  startOfMonthIsoDate,
  todayIsoDate,
} from '../reportRunnerUtils';
import styles from './MetricsPage.module.css';

interface DateRange {
  date_from: string;
  date_to: string;
}

interface KpiTile {
  label: string;
  value: string;
  tone: 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'teal';
}

function gbp(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(n);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

export function MetricsPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<DateRange>({
    date_from: startOfMonthIsoDate(),
    date_to: todayIsoDate(),
  });
  const [submitted, setSubmitted] = useState<DateRange>(form);

  const enabled = !!submitted.date_from && !!submitted.date_to;

  const statusQuery = useRunReport(
    'bookings_by_status',
    submitted as unknown as Record<string, string>,
    enabled,
  );
  const sourceQuery = useRunReport(
    'bookings_by_source',
    submitted as unknown as Record<string, string>,
    enabled,
  );
  const driverQuery = useRunReport(
    'bookings_by_driver',
    submitted as unknown as Record<string, string>,
    enabled,
  );
  const monthlyQuery = useRunReport(
    'monthly_summary',
    submitted as unknown as Record<string, string>,
    enabled,
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted({ ...form });
  }

  const status = (statusQuery.data ?? null) as ReportTable | null;
  const source = (sourceQuery.data ?? null) as ReportTable | null;
  const driver = (driverQuery.data ?? null) as ReportTable | null;
  const monthly = (monthlyQuery.data ?? null) as ReportTable | null;

  // KPI calculation — aggregate the per-status report.
  const kpis = useMemo<KpiTile[]>(() => {
    if (!status) return [];
    let total = 0;
    let completed = 0;
    let cancelled = 0;
    let pending = 0;
    let inProgress = 0;
    let revenue = 0;
    for (const row of status.rows) {
      const count = Number(row.bookings ?? 0);
      const rev = Number(row.total_revenue ?? 0);
      total += count;
      revenue += rev;
      const s = String(row.status ?? '').toLowerCase();
      if (s === 'completed') completed += count;
      else if (s === 'cancelled') cancelled += count;
      else if (s === 'pending') pending += count;
      else if (s === 'in_progress') inProgress += count;
    }
    return [
      { label: 'Total bookings', value: formatNumber(total), tone: 'blue' },
      { label: 'Completed', value: formatNumber(completed), tone: 'green' },
      { label: 'In progress', value: formatNumber(inProgress), tone: 'teal' },
      { label: 'Pending', value: formatNumber(pending), tone: 'amber' },
      { label: 'Cancelled', value: formatNumber(cancelled), tone: 'red' },
      { label: 'Revenue (booked)', value: gbp(revenue), tone: 'purple' },
    ];
  }, [status]);

  const isLoading =
    statusQuery.isFetching ||
    sourceQuery.isFetching ||
    driverQuery.isFetching ||
    monthlyQuery.isFetching;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Metrics"
        subtitle="Operational KPIs and breakdowns over a date range"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/reports')}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form onSubmit={handleSubmit} className={styles.filters}>
          <label className={styles.dateField}>
            <span>Date from</span>
            <input
              type="date"
              value={form.date_from}
              onChange={(e) =>
                setForm((f) => ({ ...f, date_from: e.target.value }))
              }
              required
            />
          </label>
          <label className={styles.dateField}>
            <span>Date to</span>
            <input
              type="date"
              value={form.date_to}
              onChange={(e) =>
                setForm((f) => ({ ...f, date_to: e.target.value }))
              }
              required
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            icon={RefreshCw}
            loading={isLoading}
          >
            Refresh
          </Button>
        </form>
      </Card>

      {isLoading && !status ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {kpis.length > 0 ? (
        <ul className={styles.kpiGrid}>
          {kpis.map((k) => (
            <li key={k.label} className={`${styles.kpi} ${styles[`tone_${k.tone}`]}`}>
              <span className={styles.kpiLabel}>{k.label}</span>
              <span className={styles.kpiValue}>{k.value}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {status ? (
        <Card title="Bookings by status">
          <BarChart
            rows={status.rows}
            labelKey="status"
            valueKey="bookings"
            colour="var(--color-secondary, #2563eb)"
          />
        </Card>
      ) : null}

      {source ? (
        <Card title="Bookings by source">
          <BarChart
            rows={source.rows}
            labelKey="source_name"
            valueKey="bookings"
            colour="var(--color-status-confirmed_text, #1e40af)"
          />
        </Card>
      ) : null}

      {driver ? (
        <Card title="Bookings by driver" flush>
          <BasicTable table={driver} />
        </Card>
      ) : null}

      {monthly ? (
        <Card title="Monthly summary" flush>
          <BasicTable table={monthly} />
        </Card>
      ) : null}

      {!isLoading && !status ? (
        <Card>
          <p className={styles.empty}>
            <BarChart3 size={20} aria-hidden /> Pick a date range and refresh to
            see metrics.
          </p>
        </Card>
      ) : null}
    </div>
  );
}

interface BarChartProps {
  rows: Record<string, string | number | null>[];
  labelKey: string;
  valueKey: string;
  colour: string;
}

function BarChart({ rows, labelKey, valueKey, colour }: BarChartProps): JSX.Element {
  if (rows.length === 0) {
    return <p className={styles.empty}>No data for this period.</p>;
  }
  const max = rows.reduce((acc, r) => {
    const v = Number(r[valueKey] ?? 0);
    return v > acc ? v : acc;
  }, 0);
  return (
    <ul className={styles.barList}>
      {rows.map((row, idx) => {
        const value = Number(row[valueKey] ?? 0);
        const pct = max === 0 ? 0 : Math.max(2, Math.round((value / max) * 100));
        const label = String(row[labelKey] ?? '—');
        return (
          <li key={`${label}-${idx}`} className={styles.barRow}>
            <span className={styles.barLabel} title={label}>
              {label}
            </span>
            <div className={styles.barTrack}>
              <span
                className={styles.barFill}
                style={{ width: `${pct}%`, background: colour }}
              />
            </div>
            <span className={styles.barValue}>{formatNumber(value)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function BasicTable({ table }: { table: ReportTable }): JSX.Element {
  if (table.rows.length === 0) {
    return <p className={styles.empty}>No data for this period.</p>;
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, idx) => (
            <tr key={idx}>
              {table.columns.map((c) => (
                <td key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                  {formatCell(row[c.key] ?? null, c.type)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
